/**
 * @file SenderWarehousePicker.tsx
 * @description Кнопка «Подтянуть из склада» для адреса отправления
 * (phase19.12, требование пользователя).
 *
 * Адрес склада уже заведён в WMS — перепечатывать его руками незачем. Один
 * подходящий склад подставляется сразу, из нескольких менеджер выбирает в
 * списке; склады без адреса видны, но недоступны — чтобы было понятно, что
 * чинить в WMS.
 */

import { Loader2, Warehouse } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { warehousesApi, type WarehouseDto } from "../../lib/api-wms";

interface Props {
  disabled?: boolean;
  /** Возвращает адрес выбранного склада строкой. */
  onPick: (address: string) => void;
}

export function SenderWarehousePicker({ disabled, onPick }: Props) {
  const [items, setItems] = useState<WarehouseDto[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    void warehousesApi
      .list()
      .then((list) => {
        if (!alive) return;
        // Дефолтный склад — первым: в 90% случаев отгружают именно с него.
        const active = list.filter((w) => w.isActive);
        setItems(
          [...active].sort((a, b) => Number(b.isDefault) - Number(a.isDefault)),
        );
      })
      .catch(() => {
        // Склады не обязательны для настройки перевозчика — просто прячем кнопку.
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const withAddress = items.filter((w) => (w.address ?? "").trim() !== "");
  if (loading || withAddress.length === 0) return null;

  const pick = (warehouse: WarehouseDto): void => {
    setOpen(false);
    onPick((warehouse.address ?? "").trim());
  };

  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-11 self-start"
        disabled={disabled}
        onClick={() => (withAddress.length === 1 ? pick(withAddress[0]!) : setOpen((v) => !v))}
      >
        {loading ? (
          <Loader2 className="mr-1 size-4 animate-spin" />
        ) : (
          <Warehouse className="mr-1 size-4" />
        )}
        Подтянуть из склада
      </Button>

      {open ? (
        <ul className="flex flex-col rounded-lg [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
          {items.map((w) => {
            const address = (w.address ?? "").trim();
            return (
              <li key={w.id}>
                <button
                  type="button"
                  disabled={!address || disabled}
                  onClick={() => pick(w)}
                  className="flex min-h-11 w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors enabled:hover:bg-surface-low disabled:opacity-50"
                >
                  <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {w.name}
                    {w.isDefault ? <Badge variant="secondary">по умолчанию</Badge> : null}
                  </span>
                  <span className="text-xs text-on-surface-variant">
                    {address || "адрес не заполнен"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
